/**
 * Gather Tutorial Page
 */

import { useI18n, Locale } from '@/lib/i18n';
import { TutorialTemplate, TutorialData } from './TutorialTemplate';

const translations: Record<Locale, TutorialData> = {
  en: {
    pageTitle: 'Using Sokuji with Gather',
    backLink: 'Back to Supported Sites',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Overview',
      content: 'This guide will walk you through setting up and using Sokuji with Gather (Gather.town) for real-time language translation in your virtual spaces.',
    },
    steps: [
      {
        title: 'Install Sokuji Extension',
        content: 'Make sure you have the Sokuji extension installed in your Chrome or Edge browser. You can get it from the Chrome Web Store.',
        tip: 'The extension icon should appear in your browser toolbar after installation.',
      },
      {
        title: 'Open Gather',
        content: 'Navigate to <code>gather.town</code> and enter your virtual space.',
        tip: 'Gather works entirely in the browser, making it perfect for use with Sokuji.',
      },
      {
        title: 'Join Your Space',
        content: 'Sign in and enter your Gather space. You\'ll appear as an avatar in the virtual environment.',
        tip: 'Make sure your browser has permission to use your microphone.',
      },
      {
        title: 'Configure Audio Settings',
        content: 'Click on your avatar or the settings menu in Gather. Navigate to Audio/Video settings and select <strong>Sokuji Virtual Microphone</strong> as your input device.',
        tip: 'If the virtual microphone doesn\'t appear, refresh the page and ensure the Sokuji extension is active.',
      },
      {
        title: 'Start Speaking',
        content: 'Walk your avatar close to other participants to start a conversation. Your speech will be translated in real-time as you speak.',
        tip: 'In Gather, you can only hear and speak with people who are nearby your avatar.',
      },
    ],
    tips: {
      title: 'Tips for Best Results',
      items: [
        'Use headphones to prevent audio feedback in the virtual space',
        'Test your setup in a quiet area of your Gather space first',
        'Keep sentences concise for more accurate translations',
      ],
    },
    faq: {
      title: 'Frequently Asked Questions',
      items: [
        {
          question: 'Does Sokuji work in Gather private areas?',
          answer: 'Yes, Sokuji works in all Gather areas including private spaces, meeting rooms, and open areas.',
        },
        {
          question: 'Can I use Sokuji in Gather presentations?',
          answer: 'Yes, Sokuji works during Gather presentations and broadcasts where your audio is shared with participants.',
        },
        {
          question: 'Will others hear both my original voice and translation?',
          answer: 'By default, participants only hear the translated audio. If you want both, turn on Passthrough in Sokuji\'s Speech block, under Translation the other side hears: your own voice is mixed in under the translation, at 20% volume by default.',
        },
      ],
    },
    troubleshooting: {
      title: 'Troubleshooting',
      content: 'If you encounter any issues, please visit our <a href="https://github.com/kizuna-ai-lab/sokuji">GitHub repository</a> or contact us at <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> for support.',
    },
  },
  zh: {
    pageTitle: '在 Gather 中使用 Sokuji',
    backLink: '返回支持的网站',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: '概述',
      content: '本指南将指导您如何在 Gather (Gather.town) 中设置和使用 Sokuji，实现虚拟空间中的实时语言翻译。',
    },
    steps: [
      {
        title: '安装 Sokuji 扩展',
        content: '确保您已在 Chrome 或 Edge 浏览器中安装了 Sokuji 扩展。您可以从 Chrome 网上应用店获取。',
        tip: '安装后，扩展图标应出现在浏览器工具栏中。',
      },
      {
        title: '打开 Gather',
        content: '访问 <code>gather.town</code> 并进入您的虚拟空间。',
        tip: 'Gather 完全在浏览器中运行，非常适合与 Sokuji 一起使用。',
      },
      {
        title: '加入空间',
        content: '登录并进入您的 Gather 空间。您将以虚拟形象出现在虚拟环境中。',
        tip: '确保您的浏览器有权限使用您的麦克风。',
      },
      {
        title: '配置音频设置',
        content: '点击您的虚拟形象或 Gather 中的设置菜单。导航到音频/视频设置，并选择 <strong>Sokuji Virtual Microphone</strong> 作为您的输入设备。',
        tip: '如果虚拟麦克风没有出现，请刷新页面并确保 Sokuji 扩展处于活动状态。',
      },
      {
        title: '开始说话',
        content: '将您的虚拟形象走近其他参与者以开始对话。您的语音将在您说话时被实时翻译。',
        tip: '在 Gather 中，您只能与靠近您虚拟形象的人听到和说话。',
      },
    ],
    tips: {
      title: '最佳使用建议',
      items: [
        '使用耳机以防止虚拟空间中的音频反馈',
        '首先在 Gather 空间的安静区域测试您的设置',
        '保持句子简洁以获得更准确的翻译',
      ],
    },
    faq: {
      title: '常见问题',
      items: [
        {
          question: 'Sokuji 在 Gather 私人区域中有效吗？',
          answer: '是的，Sokuji 在所有 Gather 区域中都有效，包括私人空间、会议室和开放区域。',
        },
        {
          question: '我可以在 Gather 演示中使用 Sokuji 吗？',
          answer: '是的，Sokuji 在 Gather 演示和广播期间有效，您的音频与参与者共享。',
        },
        {
          question: '其他人会同时听到我的原声和翻译吗？',
          answer: '默认情况下，参与者只能听到翻译后的音频。如果您想要两者都有，请在 Sokuji 语音区块中“对方听到的翻译”下打开“原声直通”：您的原声会以较低音量（默认 20%）混在译音下面。',
        },
      ],
    },
    troubleshooting: {
      title: '故障排除',
      content: '如果遇到任何问题，请访问我们的 <a href="https://github.com/kizuna-ai-lab/sokuji">GitHub 仓库</a> 或发送邮件至 <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> 获取支持。',
    },
  },
  ja: {
    pageTitle: 'Gather で Sokuji を使用する',
    backLink: 'サポートされているサイトに戻る',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: '概要',
      content: 'このガイドでは、Gather (Gather.town) で Sokuji を設定して使用し、バーチャルスペースでリアルタイム言語翻訳を行う方法を説明します。',
    },
    steps: [
      {
        title: 'Sokuji 拡張機能をインストール',
        content: 'Chrome または Edge ブラウザに Sokuji 拡張機能がインストールされていることを確認してください。Chrome ウェブストアから入手できます。',
        tip: 'インストール後、拡張機能のアイコンがブラウザのツールバーに表示されます。',
      },
      {
        title: 'Gather を開く',
        content: '<code>gather.town</code> にアクセスし、バーチャルスペースに入ります。',
        tip: 'Gather は完全にブラウザで動作するため、Sokuji との使用に最適です。',
      },
      {
        title: 'スペースに参加',
        content: 'サインインして Gather スペースに入ります。バーチャル環境でアバターとして表示されます。',
        tip: 'ブラウザがマイクを使用する権限を持っていることを確認してください。',
      },
      {
        title: 'オーディオ設定を構成',
        content: 'Gather でアバターまたは設定メニューをクリックします。オーディオ/ビデオ設定に移動し、入力デバイスとして <strong>Sokuji Virtual Microphone</strong> を選択します。',
        tip: '仮想マイクが表示されない場合は、ページを更新し、Sokuji 拡張機能がアクティブであることを確認してください。',
      },
      {
        title: '話し始める',
        content: '他の参加者の近くにアバターを歩かせて会話を開始します。話すと音声がリアルタイムで翻訳されます。',
        tip: 'Gather では、アバターの近くにいる人とのみ聞いたり話したりできます。',
      },
    ],
    tips: {
      title: '最良の結果を得るためのヒント',
      items: [
        'バーチャルスペースでのオーディオフィードバックを防ぐためにヘッドフォンを使用する',
        '最初に Gather スペースの静かなエリアで設定をテストする',
        'より正確な翻訳のために文を簡潔に保つ',
      ],
    },
    faq: {
      title: 'よくある質問',
      items: [
        {
          question: 'Sokuji は Gather のプライベートエリアで動作しますか？',
          answer: 'はい、Sokuji はプライベートスペース、会議室、オープンエリアを含むすべての Gather エリアで動作します。',
        },
        {
          question: 'Gather のプレゼンテーションで Sokuji を使用できますか？',
          answer: 'はい、Sokuji は参加者と音声が共有される Gather のプレゼンテーションやブロードキャスト中に動作します。',
        },
        {
          question: '他の人は私の元の声と翻訳の両方を聞きますか？',
          answer:
            'デフォルトでは、参加者は翻訳された音声のみを聞きます。両方を聞かせたい場合は、Sokuji の「音声」ブロックで「相手に聞こえる翻訳」の下にある「原音パススルー」をオンにしてください。あなた自身の声が翻訳の下に混ざります（デフォルトの音量は 20%）。',
        },
      ],
    },
    troubleshooting: {
      title: 'トラブルシューティング',
      content: '問題が発生した場合は、<a href="https://github.com/kizuna-ai-lab/sokuji">GitHub リポジトリ</a>を訪問するか、<a href="mailto:support@kizuna.ai">support@kizuna.ai</a> までメールでお問い合わせください。',
    },
  },
  ko: {
    pageTitle: 'Gather에서 Sokuji 사용하기',
    backLink: '지원 사이트로 돌아가기',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: '개요',
      content: '이 가이드는 Gather (Gather.town)에서 Sokuji를 설정하고 사용하여 가상 공간에서 실시간 언어 번역을 수행하는 방법을 안내합니다.',
    },
    steps: [
      {
        title: 'Sokuji 확장 프로그램 설치',
        content: 'Chrome 또는 Edge 브라우저에 Sokuji 확장 프로그램이 설치되어 있는지 확인하세요. Chrome 웹 스토어에서 받을 수 있습니다.',
        tip: '설치 후 확장 프로그램 아이콘이 브라우저 도구 모음에 나타납니다.',
      },
      {
        title: 'Gather 열기',
        content: '<code>gather.town</code>으로 이동하여 가상 공간에 입장하세요.',
        tip: 'Gather는 전적으로 브라우저에서 작동하므로 Sokuji와 함께 사용하기에 완벽합니다.',
      },
      {
        title: '공간 참여',
        content: '로그인하여 Gather 공간에 입장하세요. 가상 환경에서 아바타로 나타납니다.',
        tip: '브라우저가 마이크를 사용할 권한이 있는지 확인하세요.',
      },
      {
        title: '오디오 설정 구성',
        content: 'Gather에서 아바타나 설정 메뉴를 클릭하세요. 오디오/비디오 설정으로 이동하여 입력 장치로 <strong>Sokuji Virtual Microphone</strong>을 선택하세요.',
        tip: '가상 마이크가 나타나지 않으면 페이지를 새로 고치고 Sokuji 확장 프로그램이 활성화되어 있는지 확인하세요.',
      },
      {
        title: '말하기 시작',
        content: '대화를 시작하려면 아바타를 다른 참가자 가까이로 이동하세요. 말할 때 음성이 실시간으로 번역됩니다.',
        tip: 'Gather에서는 아바타 근처에 있는 사람들과만 듣고 말할 수 있습니다.',
      },
    ],
    tips: {
      title: '최상의 결과를 위한 팁',
      items: [
        '가상 공간에서 오디오 피드백을 방지하기 위해 헤드폰 사용',
        '먼저 Gather 공간의 조용한 구역에서 설정 테스트',
        '더 정확한 번역을 위해 문장을 간결하게 유지',
      ],
    },
    faq: {
      title: '자주 묻는 질문',
      items: [
        {
          question: 'Sokuji가 Gather 비공개 영역에서 작동하나요?',
          answer: '네, Sokuji는 비공개 공간, 회의실, 개방 영역을 포함한 모든 Gather 영역에서 작동합니다.',
        },
        {
          question: 'Gather 프레젠테이션에서 Sokuji를 사용할 수 있나요?',
          answer: '네, Sokuji는 오디오가 참가자와 공유되는 Gather 프레젠테이션과 브로드캐스트 중에 작동합니다.',
        },
        {
          question: '다른 사람들이 내 원래 목소리와 번역을 모두 듣나요?',
          answer:
            '기본적으로 참가자들은 번역된 오디오만 듣습니다. 둘 다 듣게 하려면 Sokuji의 \'음성\' 블록에서 \'상대방에게 들리는 번역\' 아래의 \'원음 패스스루\'를 켜세요. 당신의 목소리가 번역 아래에 섞여 들어갑니다(기본 음량 20%).',
        },
      ],
    },
    troubleshooting: {
      title: '문제 해결',
      content: '문제가 발생하면 <a href="https://github.com/kizuna-ai-lab/sokuji">GitHub 저장소</a>를 방문하거나 <a href="mailto:support@kizuna.ai">support@kizuna.ai</a>로 이메일을 보내 지원을 받으세요.',
    },
  },
  es: {
    pageTitle: 'Usar Sokuji con Gather',
    backLink: 'Volver a sitios compatibles',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Descripción general',
      content: 'Esta guía le explicará cómo configurar y usar Sokuji con Gather (Gather.town) para la traducción de idiomas en tiempo real en sus espacios virtuales.',
    },
    steps: [
      {
        title: 'Instalar la extensión Sokuji',
        content: 'Asegúrese de tener la extensión Sokuji instalada en su navegador Chrome o Edge. Puede obtenerla en la Chrome Web Store.',
        tip: 'El icono de la extensión debería aparecer en la barra de herramientas de su navegador después de la instalación.',
      },
      {
        title: 'Abrir Gather',
        content: 'Navegue a <code>gather.town</code> e ingrese a su espacio virtual.',
        tip: 'Gather funciona completamente en el navegador, lo que lo hace perfecto para usar con Sokuji.',
      },
      {
        title: 'Unirse a su espacio',
        content: 'Inicie sesión e ingrese a su espacio de Gather. Aparecerá como un avatar en el entorno virtual.',
        tip: 'Asegúrese de que su navegador tenga permiso para usar su micrófono.',
      },
      {
        title: 'Configurar los ajustes de audio',
        content: 'Haga clic en su avatar o en el menú de configuración en Gather. Navegue a los ajustes de Audio/Video y seleccione <strong>Sokuji Virtual Microphone</strong> como su dispositivo de entrada.',
        tip: 'Si el micrófono virtual no aparece, actualice la página y asegúrese de que la extensión Sokuji esté activa.',
      },
      {
        title: 'Empezar a hablar',
        content: 'Acerque su avatar a otros participantes para iniciar una conversación. Su voz será traducida en tiempo real mientras habla.',
        tip: 'En Gather, solo puede escuchar y hablar con personas que estén cerca de su avatar.',
      },
    ],
    tips: {
      title: 'Consejos para mejores resultados',
      items: [
        'Use auriculares para evitar la retroalimentación de audio en el espacio virtual',
        'Pruebe su configuración primero en un área tranquila de su espacio de Gather',
        'Mantenga las oraciones concisas para traducciones más precisas',
      ],
    },
    faq: {
      title: 'Preguntas frecuentes',
      items: [
        {
          question: '¿Sokuji funciona en áreas privadas de Gather?',
          answer: 'Sí, Sokuji funciona en todas las áreas de Gather, incluidos espacios privados, salas de reuniones y áreas abiertas.',
        },
        {
          question: '¿Puedo usar Sokuji en presentaciones de Gather?',
          answer: 'Sí, Sokuji funciona durante las presentaciones y transmisiones de Gather donde su audio se comparte con los participantes.',
        },
        {
          question: '¿Los demás escucharán tanto mi voz original como la traducción?',
          answer:
            'De forma predeterminada, los participantes solo escuchan el audio traducido. Si desea ambos, active "Paso directo" en el bloque "Voz" de Sokuji, bajo "Traducción que oye la otra parte": su propia voz se mezcla por debajo de la traducción, al 20 % de volumen por defecto.',
        },
      ],
    },
    troubleshooting: {
      title: 'Solución de problemas',
      content: 'Si encuentra algún problema, visite nuestro <a href="https://github.com/kizuna-ai-lab/sokuji">repositorio de GitHub</a> o contáctenos en <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> para obtener ayuda.',
    },
  },
  fr: {
    pageTitle: 'Utiliser Sokuji avec Gather',
    backLink: 'Retour aux sites pris en charge',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Aperçu',
      content: 'Ce guide vous expliquera comment configurer et utiliser Sokuji avec Gather (Gather.town) pour la traduction linguistique en temps réel dans vos espaces virtuels.',
    },
    steps: [
      {
        title: 'Installer l\'extension Sokuji',
        content: 'Assurez-vous que l\'extension Sokuji est installée dans votre navigateur Chrome ou Edge. Vous pouvez l\'obtenir sur le Chrome Web Store.',
        tip: 'L\'icône de l\'extension devrait apparaître dans la barre d\'outils de votre navigateur après l\'installation.',
      },
      {
        title: 'Ouvrir Gather',
        content: 'Accédez à <code>gather.town</code> et entrez dans votre espace virtuel.',
        tip: 'Gather fonctionne entièrement dans le navigateur, ce qui le rend parfait pour une utilisation avec Sokuji.',
      },
      {
        title: 'Rejoindre votre espace',
        content: 'Connectez-vous et entrez dans votre espace Gather. Vous apparaîtrez sous forme d\'avatar dans l\'environnement virtuel.',
        tip: 'Assurez-vous que votre navigateur a la permission d\'utiliser votre microphone.',
      },
      {
        title: 'Configurer les paramètres audio',
        content: 'Cliquez sur votre avatar ou le menu des paramètres dans Gather. Accédez aux paramètres Audio/Vidéo et sélectionnez <strong>Sokuji Virtual Microphone</strong> comme périphérique d\'entrée.',
        tip: 'Si le microphone virtuel n\'apparaît pas, actualisez la page et assurez-vous que l\'extension Sokuji est active.',
      },
      {
        title: 'Commencer à parler',
        content: 'Déplacez votre avatar près des autres participants pour commencer une conversation. Votre voix sera traduite en temps réel pendant que vous parlez.',
        tip: 'Dans Gather, vous ne pouvez entendre et parler qu\'avec les personnes proches de votre avatar.',
      },
    ],
    tips: {
      title: 'Conseils pour de meilleurs résultats',
      items: [
        'Utilisez des écouteurs pour éviter le retour audio dans l\'espace virtuel',
        'Testez d\'abord votre configuration dans une zone calme de votre espace Gather',
        'Gardez les phrases concises pour des traductions plus précises',
      ],
    },
    faq: {
      title: 'Questions fréquemment posées',
      items: [
        {
          question: 'Sokuji fonctionne-t-il dans les zones privées de Gather ?',
          answer: 'Oui, Sokuji fonctionne dans toutes les zones de Gather, y compris les espaces privés, les salles de réunion et les zones ouvertes.',
        },
        {
          question: 'Puis-je utiliser Sokuji dans les présentations Gather ?',
          answer: 'Oui, Sokuji fonctionne pendant les présentations et diffusions Gather où votre audio est partagé avec les participants.',
        },
        {
          question: 'Les autres entendront-ils à la fois ma voix originale et la traduction ?',
          answer:
            'Par défaut, les participants n\'entendent que l\'audio traduit. Si vous voulez les deux, activez « Passage direct » dans le bloc « Voix » de Sokuji, sous « Traduction entendue par l\'autre » : votre propre voix est mélangée sous la traduction, à 20 % du volume par défaut.',
        },
      ],
    },
    troubleshooting: {
      title: 'Dépannage',
      content: 'Si vous rencontrez des problèmes, veuillez visiter notre <a href="https://github.com/kizuna-ai-lab/sokuji">dépôt GitHub</a> ou nous contacter à <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> pour obtenir de l\'aide.',
    },
  },
  ar: {
    pageTitle: 'استخدام Sokuji مع Gather',
    backLink: 'العودة إلى المواقع المدعومة',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'نظرة عامة',
      content: 'سيرشدك هذا الدليل خلال إعداد واستخدام Sokuji مع Gather (Gather.town) للترجمة اللغوية الفورية في مساحاتك الافتراضية.',
    },
    steps: [
      {
        title: 'تثبيت إضافة Sokuji',
        content: 'تأكد من تثبيت إضافة Sokuji في متصفح Chrome أو Edge. يمكنك الحصول عليها من سوق Chrome الإلكتروني.',
        tip: 'يجب أن يظهر رمز الإضافة في شريط أدوات المتصفح بعد التثبيت.',
      },
      {
        title: 'فتح Gather',
        content: 'انتقل إلى <code>gather.town</code> وادخل إلى مساحتك الافتراضية.',
        tip: 'يعمل Gather بالكامل في المتصفح، مما يجعله مثاليًا للاستخدام مع Sokuji.',
      },
      {
        title: 'الانضمام إلى مساحتك',
        content: 'قم بتسجيل الدخول وادخل إلى مساحة Gather الخاصة بك. ستظهر كشخصية رمزية في البيئة الافتراضية.',
        tip: 'تأكد من أن متصفحك لديه إذن لاستخدام الميكروفون.',
      },
      {
        title: 'تكوين إعدادات الصوت',
        content: 'انقر على شخصيتك الرمزية أو قائمة الإعدادات في Gather. انتقل إلى إعدادات الصوت/الفيديو واختر <strong>Sokuji Virtual Microphone</strong> كجهاز إدخال.',
        tip: 'إذا لم يظهر الميكروفون الافتراضي، قم بتحديث الصفحة وتأكد من أن إضافة Sokuji نشطة.',
      },
      {
        title: 'ابدأ بالتحدث',
        content: 'حرّك شخصيتك الرمزية بالقرب من المشاركين الآخرين لبدء محادثة. سيتم ترجمة كلامك في الوقت الفعلي أثناء تحدثك.',
        tip: 'في Gather، يمكنك فقط سماع والتحدث مع الأشخاص القريبين من شخصيتك الرمزية.',
      },
    ],
    tips: {
      title: 'نصائح للحصول على أفضل النتائج',
      items: [
        'استخدم سماعات الرأس لمنع ارتداد الصوت في المساحة الافتراضية',
        'اختبر إعداداتك أولاً في منطقة هادئة من مساحة Gather الخاصة بك',
        'حافظ على الجمل مختصرة لترجمات أكثر دقة',
      ],
    },
    faq: {
      title: 'الأسئلة الشائعة',
      items: [
        {
          question: 'هل يعمل Sokuji في المناطق الخاصة في Gather؟',
          answer: 'نعم، يعمل Sokuji في جميع مناطق Gather بما في ذلك المساحات الخاصة وغرف الاجتماعات والمناطق المفتوحة.',
        },
        {
          question: 'هل يمكنني استخدام Sokuji في عروض Gather التقديمية؟',
          answer: 'نعم، يعمل Sokuji أثناء عروض Gather التقديمية والبث حيث يتم مشاركة صوتك مع المشاركين.',
        },
        {
          question: 'هل سيسمع الآخرون صوتي الأصلي والترجمة معًا؟',
          answer:
            'بشكل افتراضي، يسمع المشاركون الصوت المترجم فقط. إذا كنت تريد الاثنين معًا، ففعّل "التمرير المباشر" في قسم "الكلام" في Sokuji، تحت "الترجمة التي يسمعها الطرف الآخر": يُمزج صوتك تحت الترجمة بمستوى صوت 20% افتراضيًا.',
        },
      ],
    },
    troubleshooting: {
      title: 'استكشاف الأخطاء وإصلاحها',
      content: 'إذا واجهت أي مشاكل، يرجى زيارة <a href="https://github.com/kizuna-ai-lab/sokuji">مستودع GitHub</a> الخاص بنا أو التواصل معنا عبر <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> للحصول على الدعم.',
    },
  },
  pt: {
    pageTitle: 'Usando o Sokuji com o Gather',
    backLink: 'Voltar para sites compatíveis',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Visão geral',
      content: 'Este guia vai orientá-lo na configuração e uso do Sokuji com o Gather (Gather.town) para tradução de idiomas em tempo real nos seus espaços virtuais.',
    },
    steps: [
      {
        title: 'Instalar a extensão Sokuji',
        content: 'Certifique-se de que a extensão Sokuji está instalada no seu navegador Chrome ou Edge. Você pode obtê-la na Chrome Web Store.',
        tip: 'O ícone da extensão deve aparecer na barra de ferramentas do seu navegador após a instalação.',
      },
      {
        title: 'Abrir o Gather',
        content: 'Navegue até <code>gather.town</code> e entre no seu espaço virtual.',
        tip: 'O Gather funciona inteiramente no navegador, tornando-o perfeito para usar com o Sokuji.',
      },
      {
        title: 'Entrar no seu espaço',
        content: 'Faça login e entre no seu espaço Gather. Você aparecerá como um avatar no ambiente virtual.',
        tip: 'Certifique-se de que seu navegador tem permissão para usar seu microfone.',
      },
      {
        title: 'Configurar as definições de áudio',
        content: 'Clique no seu avatar ou no menu de configurações no Gather. Navegue até as configurações de Áudio/Vídeo e selecione <strong>Sokuji Virtual Microphone</strong> como seu dispositivo de entrada.',
        tip: 'Se o microfone virtual não aparecer, atualize a página e certifique-se de que a extensão Sokuji está ativa.',
      },
      {
        title: 'Começar a falar',
        content: 'Mova seu avatar para perto de outros participantes para iniciar uma conversa. Sua fala será traduzida em tempo real enquanto você fala.',
        tip: 'No Gather, você só pode ouvir e falar com pessoas que estão perto do seu avatar.',
      },
    ],
    tips: {
      title: 'Dicas para melhores resultados',
      items: [
        'Use fones de ouvido para evitar feedback de áudio no espaço virtual',
        'Teste sua configuração primeiro em uma área tranquila do seu espaço Gather',
        'Mantenha as frases concisas para traduções mais precisas',
      ],
    },
    faq: {
      title: 'Perguntas frequentes',
      items: [
        {
          question: 'O Sokuji funciona em áreas privadas do Gather?',
          answer: 'Sim, o Sokuji funciona em todas as áreas do Gather, incluindo espaços privados, salas de reunião e áreas abertas.',
        },
        {
          question: 'Posso usar o Sokuji em apresentações do Gather?',
          answer: 'Sim, o Sokuji funciona durante apresentações e transmissões do Gather onde seu áudio é compartilhado com os participantes.',
        },
        {
          question: 'Os outros ouvirão tanto minha voz original quanto a tradução?',
          answer:
            'Por padrão, os participantes ouvem apenas o áudio traduzido. Se quiser ambos, ative "Passagem direta" no bloco "Voz" do Sokuji, abaixo de "Tradução que o outro lado ouve": sua própria voz é misturada por baixo da tradução, a 20% do volume por padrão.',
        },
      ],
    },
    troubleshooting: {
      title: 'Solução de problemas',
      content: 'Se encontrar algum problema, visite nosso <a href="https://github.com/kizuna-ai-lab/sokuji">repositório no GitHub</a> ou entre em contato conosco em <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> para obter suporte.',
    },
  },
  ru: {
    pageTitle: 'Использование Sokuji с Gather',
    backLink: 'Назад к поддерживаемым сайтам',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Обзор',
      content: 'Это руководство поможет вам настроить и использовать Sokuji с Gather (Gather.town) для перевода в реальном времени в ваших виртуальных пространствах.',
    },
    steps: [
      {
        title: 'Установите расширение Sokuji',
        content: 'Убедитесь, что расширение Sokuji установлено в вашем браузере Chrome или Edge. Вы можете получить его в Интернет-магазине Chrome.',
        tip: 'После установки значок расширения должен появиться на панели инструментов браузера.',
      },
      {
        title: 'Откройте Gather',
        content: 'Перейдите на <code>gather.town</code> и войдите в ваше виртуальное пространство.',
        tip: 'Gather работает полностью в браузере, что делает его идеальным для использования с Sokuji.',
      },
      {
        title: 'Присоединитесь к пространству',
        content: 'Войдите в систему и войдите в ваше пространство Gather. Вы появитесь в виде аватара в виртуальной среде.',
        tip: 'Убедитесь, что ваш браузер имеет разрешение на использование микрофона.',
      },
      {
        title: 'Настройте параметры аудио',
        content: 'Нажмите на ваш аватар или меню настроек в Gather. Перейдите в настройки Аудио/Видео и выберите <strong>Sokuji Virtual Microphone</strong> в качестве устройства ввода.',
        tip: 'Если виртуальный микрофон не отображается, обновите страницу и убедитесь, что расширение Sokuji активно.',
      },
      {
        title: 'Начните говорить',
        content: 'Подведите аватар к другим участникам, чтобы начать разговор. Ваша речь будет переводиться в реальном времени.',
        tip: 'В Gather вы можете слышать и говорить только с людьми, находящимися рядом с вашим аватаром.',
      },
    ],
    tips: {
      title: 'Советы для лучших результатов',
      items: [
        'Используйте наушники для предотвращения обратной связи аудио в виртуальном пространстве',
        'Сначала протестируйте настройки в тихой зоне вашего пространства Gather',
        'Формулируйте предложения кратко для более точного перевода',
      ],
    },
    faq: {
      title: 'Часто задаваемые вопросы',
      items: [
        {
          question: 'Работает ли Sokuji в приватных зонах Gather?',
          answer: 'Да, Sokuji работает во всех зонах Gather, включая приватные пространства, переговорные комнаты и открытые зоны.',
        },
        {
          question: 'Могу ли я использовать Sokuji в презентациях Gather?',
          answer: 'Да, Sokuji работает во время презентаций и трансляций Gather, где ваш звук передаётся участникам.',
        },
        {
          question: 'Будут ли другие слышать и мой оригинальный голос, и перевод?',
          answer:
            'По умолчанию участники слышат только переведённый звук. Если вы хотите и то и другое, включите «Passthrough» в блоке «Речь» в Sokuji, под строкой «Перевод, который слышит собеседник»: ваш собственный голос подмешивается под перевод, по умолчанию с громкостью 20%.',
        },
      ],
    },
    troubleshooting: {
      title: 'Устранение неполадок',
      content: 'Если вы столкнулись с проблемами, посетите наш <a href="https://github.com/kizuna-ai-lab/sokuji">репозиторий на GitHub</a> или свяжитесь с нами по адресу <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> для получения поддержки.',
    },
  },
  de: {
    pageTitle: 'Sokuji mit Gather verwenden',
    backLink: 'Zurück zu unterstützten Seiten',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Überblick',
      content: 'Diese Anleitung führt Sie durch die Einrichtung und Verwendung von Sokuji mit Gather (Gather.town) für Echtzeit-Sprachübersetzung in Ihren virtuellen Räumen.',
    },
    steps: [
      {
        title: 'Sokuji-Erweiterung installieren',
        content: 'Stellen Sie sicher, dass die Sokuji-Erweiterung in Ihrem Chrome- oder Edge-Browser installiert ist. Sie können sie im Chrome Web Store erhalten.',
        tip: 'Das Symbol der Erweiterung sollte nach der Installation in der Symbolleiste Ihres Browsers erscheinen.',
      },
      {
        title: 'Gather öffnen',
        content: 'Navigieren Sie zu <code>gather.town</code> und betreten Sie Ihren virtuellen Raum.',
        tip: 'Gather funktioniert vollständig im Browser und eignet sich daher perfekt für die Verwendung mit Sokuji.',
      },
      {
        title: 'Ihrem Raum beitreten',
        content: 'Melden Sie sich an und betreten Sie Ihren Gather-Raum. Sie erscheinen als Avatar in der virtuellen Umgebung.',
        tip: 'Stellen Sie sicher, dass Ihr Browser die Berechtigung hat, Ihr Mikrofon zu verwenden.',
      },
      {
        title: 'Audioeinstellungen konfigurieren',
        content: 'Klicken Sie auf Ihren Avatar oder das Einstellungsmenü in Gather. Navigieren Sie zu den Audio-/Videoeinstellungen und wählen Sie <strong>Sokuji Virtual Microphone</strong> als Eingabegerät.',
        tip: 'Wenn das virtuelle Mikrofon nicht angezeigt wird, aktualisieren Sie die Seite und stellen Sie sicher, dass die Sokuji-Erweiterung aktiv ist.',
      },
      {
        title: 'Sprechen Sie los',
        content: 'Bewegen Sie Ihren Avatar in die Nähe anderer Teilnehmer, um ein Gespräch zu beginnen. Ihre Sprache wird in Echtzeit übersetzt, während Sie sprechen.',
        tip: 'In Gather können Sie nur Personen hören und mit ihnen sprechen, die sich in der Nähe Ihres Avatars befinden.',
      },
    ],
    tips: {
      title: 'Tipps für beste Ergebnisse',
      items: [
        'Verwenden Sie Kopfhörer, um Audio-Rückkopplung im virtuellen Raum zu vermeiden',
        'Testen Sie Ihre Einrichtung zuerst in einem ruhigen Bereich Ihres Gather-Raums',
        'Halten Sie Sätze kurz und prägnant für genauere Übersetzungen',
      ],
    },
    faq: {
      title: 'Häufig gestellte Fragen',
      items: [
        {
          question: 'Funktioniert Sokuji in privaten Bereichen von Gather?',
          answer: 'Ja, Sokuji funktioniert in allen Gather-Bereichen einschließlich privater Räume, Besprechungsräume und offener Bereiche.',
        },
        {
          question: 'Kann ich Sokuji in Gather-Präsentationen verwenden?',
          answer: 'Ja, Sokuji funktioniert während Gather-Präsentationen und Übertragungen, bei denen Ihr Audio mit den Teilnehmern geteilt wird.',
        },
        {
          question: 'Werden andere sowohl meine Originalstimme als auch die Übersetzung hören?',
          answer:
            'Standardmäßig hören Teilnehmer nur das übersetzte Audio. Wenn Sie beides möchten, schalten Sie in Sokujis Block „Sprache“ unter „Übersetzung, die das Gegenüber hört“ die „Durchleitung“ ein: Ihre eigene Stimme wird leiser unter die Übersetzung gemischt, standardmäßig mit 20 % Lautstärke.',
        },
      ],
    },
    troubleshooting: {
      title: 'Fehlerbehebung',
      content: 'Wenn Sie auf Probleme stoßen, besuchen Sie bitte unser <a href="https://github.com/kizuna-ai-lab/sokuji">GitHub-Repository</a> oder kontaktieren Sie uns unter <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> für Unterstützung.',
    },
  },
  uk: {
    pageTitle: 'Використання Sokuji з Gather',
    backLink: 'Назад до підтримуваних сайтів',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Огляд',
      content: 'Цей посібник допоможе вам налаштувати та використовувати Sokuji з Gather (Gather.town) для перекладу мов у реальному часі у ваших віртуальних просторах.',
    },
    steps: [
      {
        title: 'Встановіть розширення Sokuji',
        content: 'Переконайтеся, що розширення Sokuji встановлено у вашому браузері Chrome або Edge. Ви можете отримати його в Інтернет-магазині Chrome.',
        tip: 'Після встановлення значок розширення повинен з\'явитися на панелі інструментів браузера.',
      },
      {
        title: 'Відкрийте Gather',
        content: 'Перейдіть на <code>gather.town</code> та увійдіть до вашого віртуального простору.',
        tip: 'Gather працює повністю в браузері, що робить його ідеальним для використання з Sokuji.',
      },
      {
        title: 'Приєднайтеся до простору',
        content: 'Увійдіть у систему та увійдіть до вашого простору Gather. Ви з\'явитесь як аватар у віртуальному середовищі.',
        tip: 'Переконайтеся, що ваш браузер має дозвіл на використання мікрофона.',
      },
      {
        title: 'Налаштуйте параметри аудіо',
        content: 'Натисніть на ваш аватар або меню налаштувань у Gather. Перейдіть до налаштувань Аудіо/Відео та виберіть <strong>Sokuji Virtual Microphone</strong> як пристрій введення.',
        tip: 'Якщо віртуальний мікрофон не з\'являється, оновіть сторінку та переконайтеся, що розширення Sokuji активне.',
      },
      {
        title: 'Почніть говорити',
        content: 'Підведіть аватар до інших учасників, щоб почати розмову. Ваше мовлення буде перекладатися в реальному часі.',
        tip: 'У Gather ви можете чути і говорити лише з людьми, які знаходяться поруч з вашим аватаром.',
      },
    ],
    tips: {
      title: 'Поради для кращих результатів',
      items: [
        'Використовуйте навушники для запобігання зворотного зв\'язку аудіо у віртуальному просторі',
        'Спочатку протестуйте налаштування в тихій зоні вашого простору Gather',
        'Формулюйте речення стисло для точнішого перекладу',
      ],
    },
    faq: {
      title: 'Часті запитання',
      items: [
        {
          question: 'Чи працює Sokuji в приватних зонах Gather?',
          answer: 'Так, Sokuji працює в усіх зонах Gather, включаючи приватні простори, кімнати для зустрічей та відкриті зони.',
        },
        {
          question: 'Чи можу я використовувати Sokuji в презентаціях Gather?',
          answer: 'Так, Sokuji працює під час презентацій і трансляцій Gather, де ваше аудіо передається учасникам.',
        },
        {
          question: 'Чи почують інші і мій оригінальний голос, і переклад?',
          answer:
            'За замовчуванням учасники чують лише перекладене аудіо. Якщо ви хочете і те, і інше, увімкніть «Passthrough» у блоці «Мовлення» в Sokuji, під рядком «Переклад, який чує співрозмовник»: ваш власний голос підмішується під переклад, за замовчуванням із гучністю 20%.',
        },
      ],
    },
    troubleshooting: {
      title: 'Усунення несправностей',
      content: 'Якщо у вас виникли проблеми, відвідайте наш <a href="https://github.com/kizuna-ai-lab/sokuji">репозиторій на GitHub</a> або зв\'яжіться з нами за адресою <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> для отримання підтримки.',
    },
  },
  it: {
    pageTitle: 'Usare Sokuji con Gather',
    backLink: 'Torna ai siti supportati',
    backLinkUrl: '/docs/supported-sites',
    overview: {
      title: 'Panoramica',
      content: 'Questa guida ti spiegherà come configurare e utilizzare Sokuji con Gather (Gather.town) per la traduzione linguistica in tempo reale nei tuoi spazi virtuali.',
    },
    steps: [
      {
        title: 'Installare l\'estensione Sokuji',
        content: 'Assicurati di avere l\'estensione Sokuji installata nel tuo browser Chrome o Edge. Puoi ottenerla dal Chrome Web Store.',
        tip: 'L\'icona dell\'estensione dovrebbe apparire nella barra degli strumenti del browser dopo l\'installazione.',
      },
      {
        title: 'Aprire Gather',
        content: 'Vai su <code>gather.town</code> e entra nel tuo spazio virtuale.',
        tip: 'Gather funziona interamente nel browser, rendendolo perfetto per l\'uso con Sokuji.',
      },
      {
        title: 'Entrare nel tuo spazio',
        content: 'Accedi e entra nel tuo spazio Gather. Apparirai come un avatar nell\'ambiente virtuale.',
        tip: 'Assicurati che il tuo browser abbia il permesso di usare il microfono.',
      },
      {
        title: 'Configurare le impostazioni audio',
        content: 'Fai clic sul tuo avatar o sul menu delle impostazioni in Gather. Vai alle impostazioni Audio/Video e seleziona <strong>Sokuji Virtual Microphone</strong> come dispositivo di input.',
        tip: 'Se il microfono virtuale non appare, aggiorna la pagina e assicurati che l\'estensione Sokuji sia attiva.',
      },
      {
        title: 'Iniziare a parlare',
        content: 'Avvicina il tuo avatar agli altri partecipanti per iniziare una conversazione. Il tuo discorso verrà tradotto in tempo reale mentre parli.',
        tip: 'In Gather, puoi sentire e parlare solo con le persone vicine al tuo avatar.',
      },
    ],
    tips: {
      title: 'Consigli per i migliori risultati',
      items: [
        'Usa le cuffie per evitare il feedback audio nello spazio virtuale',
        'Testa prima la tua configurazione in un\'area tranquilla del tuo spazio Gather',
        'Mantieni le frasi concise per traduzioni più accurate',
      ],
    },
    faq: {
      title: 'Domande frequenti',
      items: [
        {
          question: 'Sokuji funziona nelle aree private di Gather?',
          answer: 'Sì, Sokuji funziona in tutte le aree di Gather, compresi spazi privati, sale riunioni e aree aperte.',
        },
        {
          question: 'Posso usare Sokuji nelle presentazioni di Gather?',
          answer: 'Sì, Sokuji funziona durante le presentazioni e le trasmissioni di Gather dove il tuo audio è condiviso con i partecipanti.',
        },
        {
          question: 'Gli altri sentiranno sia la mia voce originale che la traduzione?',
          answer:
            'Per impostazione predefinita, i partecipanti sentono solo l\'audio tradotto. Se desideri entrambi, attiva “Passthrough” nel blocco “Voce” di Sokuji, sotto “Traduzione che sente l\'altra parte”: la tua voce viene mescolata sotto la traduzione, al 20% del volume per impostazione predefinita.',
        },
      ],
    },
    troubleshooting: {
      title: 'Risoluzione dei problemi',
      content: 'Se riscontri problemi, visita il nostro <a href="https://github.com/kizuna-ai-lab/sokuji">repository GitHub</a> o contattaci all\'indirizzo <a href="mailto:support@kizuna.ai">support@kizuna.ai</a> per ricevere assistenza.',
    },
  },
};

export function GatherTutorial() {
  const { locale } = useI18n();
  const data = translations[locale] || translations.en;

  return <TutorialTemplate data={data} screenshotBasePath="/tutorials/gather" />;
}
